import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  Receipt,
  UserCheck,
  BarChart3,
  User,
  LogOut,
  Stethoscope,
  Map,
  Shield,
  CalendarDays,
  Globe,
  AlertTriangle
} from "lucide-react";

// Simple createPageUrl function
const createPageUrl = (page) => `/${page.toLowerCase()}`;

// Mock user data
const mockUser = { full_name: "Shriyam Singh", role: "user" };

const navigationItems = [
  { title: "Dashboard", url: createPageUrl("Dashboard"), icon: LayoutDashboard, adminOnly: false },
  { title: "Admin Dashboard", url: createPageUrl("AdminDashboard"), icon: Shield, adminOnly: true },
  { title: "Visit Planning", url: createPageUrl("Visits"), icon: Calendar, adminOnly: false },
  { title: "Doctor Database", url: createPageUrl("Doctors"), icon: Stethoscope, adminOnly: false },
  { title: "Employees", url: createPageUrl("Employees"), icon: Users, adminOnly: true },
  { title: "Daily Reports", url: createPageUrl("Reports"), icon: FileText, adminOnly: false },
  { title: "Expenses", url: createPageUrl("Expenses"), icon: Receipt, adminOnly: false },
  { title: "HR & Leave", url: createPageUrl("HR"), icon: UserCheck, adminOnly: false },
  { title: "Holidays", url: createPageUrl("Holidays"), icon: CalendarDays, adminOnly: true },
  { title: "Analytics", url: createPageUrl("Analytics"), icon: BarChart3, adminOnly: false },
  { title: "Live Tracking", url: createPageUrl("LiveTracking"), icon: Map, adminOnly: true },
  { title: "Product Alerts", url: createPageUrl("ProductAlerts"), icon: AlertTriangle, adminOnly: true },
  { title: "Landing Page", url: createPageUrl("Landing"), icon: Globe, adminOnly: false },
];

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const [user] = React.useState(mockUser);

  const userIsAdmin = user?.role === 'admin';

  if (currentPageName === 'Landing') {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex w-full bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-64 bg-white/80 backdrop-blur-sm border-r border-blue-100">
        <div className="border-b border-blue-100 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-medium text-gray-600 text-base">MedRep CRM</h2>
              <p className="text-sm text-blue-600 font-medium">
                {userIsAdmin ? 'Admin Portal' : 'Medical Representative'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">
            Main Navigation
          </div>
          <div className="space-y-1">
            {navigationItems
              .filter(item => userIsAdmin ? true : !item.adminOnly)
              .map((item) => (
                <Link
                  key={item.title}
                  to={item.url}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                    location.pathname === item.url
                      ? 'bg-blue-50 text-blue-700 font-bold border-r-2 border-blue-600'
                      : 'font-medium hover:bg-blue-50 hover:text-blue-700'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.title}</span>
                </Link>
              ))}
          </div>
        </div>

        <div className="border-t border-blue-100 p-4 mt-auto">
          {user && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">{user.full_name}</p>
                  <p className="text-xs text-blue-600 truncate">{user.role}</p>
                </div>
              </div>
              <button className="text-gray-500 hover:text-red-600 hover:bg-red-50 p-2 rounded">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="ml-64 flex-1 flex flex-col">
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
